import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import {Box, Button, Chip, Collapse, Grid, Typography} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close'; // Added Close icon
import {IMAGE_URLS} from '../config/urls';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SortDropdown from './SortDropdown';
import CalendarIcon from './icons/CalendarIcon';
import SunElevationIcon from './icons/SunElevationIcon';
import CloudIcon from './icons/CloudIcon';
import OffNadirIcon from './icons/OffNadirIcon';
import CoverageIcon from './icons/CoverageIcon';

// Helper function to get hardcoded color for each catalog satellite
const getCatalogSatelliteColor = (satelliteName) => {
  const name = (satelliteName || '').toLowerCase();
  
  // Hardcoded colors for catalog satellites - distinct, medium saturation colors
  if (name.includes('sentinel-2') || name.includes('sentinel 2')) return '#3498DB'; // Bright Blue
  if (name.includes('landsat') || name.includes('landsat-8') || name.includes('landsat-9')) return '#2ECC71'; // Emerald Green
  if (name.includes('sentinel-1') || name.includes('sentinel 1')) return '#E67E22'; // Orange
  
  // Default color for other satellites
  return '#76b6ff';
};

// Helper function to sort catalog satellites in fixed order
const sortCatalogSatellites = (a, b) => {
  const getOrder = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('sentinel-2') || lowerName.includes('sentinel 2')) return 1;
    if (lowerName.includes('landsat')) return 2;
    if (lowerName.includes('sentinel-1') || lowerName.includes('sentinel 1')) return 3;
    return 999; // Others come last
  };
  return getOrder(a) - getOrder(b);
};

// Updated ImageCard component in CatalogImagery.js
const ImageCard = ({ data, isSelected, isVisible, onSelect, onHover }) => {
  const [isImageHovered, setIsImageHovered] = useState(false);
const isValid = (val) => val !== undefined && val !== null && !isNaN(val);

  return (
    <Box
      onMouseEnter={() => {
        setIsImageHovered(true);
        onHover?.(data);
      }}
      onMouseLeave={() => {
        setIsImageHovered(false);
        onHover?.(null);
      }}
      onClick={() => onSelect(data)}
      sx={{
        bgcolor: isSelected ? '#7C7C7C66' : 'transparent',
        border: isSelected ? '1px solid #76b6ff' : '1px solid transparent',
        borderRadius: 1,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.25,
        py: 0.5,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        '&:hover': {
          bgcolor: isSelected ? '#7C7C7C66' : 'rgba(124, 124, 124, 0.2)',
        },
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Box
          component="img"
          src={data.previewUrl || ""}
          alt={`Preview ${data.date}`}
          onMouseEnter={() => setIsImageHovered(true)}
          onMouseLeave={() => setIsImageHovered(false)}
          sx={{
            width: 90,
            height: 90,
            objectFit: 'cover',
            borderRadius: 0,
            mr: 0.25,
            display: 'block',
          }}
        />
        {isVisible && (
          <VisibilityIcon
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              opacity: 1,
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
              fontSize: 24,
            }}
          />
        )}
      </Box>
      <Box sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.7)', flex: 1, minWidth: 0 }}>
  {data.id?.includes('IS18021') ? (
    <>
              {/* Date always shown */}
      <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <CalendarIcon size={10} color="rgba(255, 255, 255, 0.7)" />
                </Box>
                <Box sx={{ whiteSpace: 'nowrap' }}>
                  {data.date}
                </Box>
      </Typography>

              {/* Coverage % */}
              {isValid(parseFloat(data.coveragePercentage)) && (
                <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CoverageIcon size={10} color="rgba(255, 255, 255, 0.7)" /> {parseFloat(data.coveragePercentage).toFixed(1)}%
                </Typography>
              )}
    </>
  ) : (
    <>
              {/* Date */}
      <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <CalendarIcon size={10} color="rgba(255, 255, 255, 0.7)" />
                </Box>
                <Box sx={{ whiteSpace: 'nowrap' }}>
                  {data.date}
                </Box>
      </Typography>

              {/* Coverage Percentage */}
              {isValid(parseFloat(data.coveragePercentage)) && (
                <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CoverageIcon size={10} color="rgba(255, 255, 255, 0.7)" /> {parseFloat(data.coveragePercentage).toFixed(1)}%
      </Typography>
              )}

              {/* Cloud cover */}
              {isValid(parseFloat(data.coverage)) && (
                <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CloudIcon size={10} color="rgba(255, 255, 255, 0.7)" /> {parseFloat(data.coverage).toFixed(1)}%
                </Typography>
              )}

              {/* Off-nadir angle */}
              {isValid(parseFloat(data.off_nadir_angle)) && (
                <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <OffNadirIcon size={10} color="rgba(255, 255, 255, 0.7)" /> {parseFloat(data.off_nadir_angle).toFixed(1)}°
                </Typography>
              )}

              {/* Sun elevation */}
              {isValid(parseFloat(data.angle)) && (
      <Typography sx={{ fontSize: 'inherit', mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SunElevationIcon size={10} color="rgba(255, 255, 255, 0.7)" /> {parseFloat(data.angle).toFixed(1)}°
      </Typography>
              )}
    </>
  )}
</Box>

    </Box>
  );
};

const CatalogImagery = ({ 
  items, 
  onImageSelect, 
  onVisibilityToggle, 
  onImageHover, 
  visibleImage,
  drawnCoordinates,
  onSatelliteVisibilityToggle,
  onSatelliteDataUpdate,
constellationFilter,
}) => {
    useEffect(() => {
      if (!constellationFilter) return;

      setSatelliteVisibility(prev =>
        Object.fromEntries(
          Object.keys(prev).map(sat => [
            sat,
            constellationFilter.includes(sat)
          ])
        )
      );
    }, [constellationFilter]);

const getBandDetails = (item) => {
  const assets = item.assets || {};
  const satellite = (item.satellite || "").toLowerCase();

  // Extract resolution from filenames like "_10m.tif"
  const extractResFromHref = (href) => {
    const match = href?.match(/_(10m|20m|60m)\.tif/i);
    return match ? match[1].replace("m", "") : null;
  };

  // Clean S2 titles like "Band 2 - Blue - 10m"
  const cleanTitle = (title = "") => {
    return title.replace(/\s-\s(10m|20m|60m)$/i, "");
  };

  // SENTINEL-1 (no title cleaning needed)
  if (satellite.includes("sentinel-1")) {
    const res = item.resolution || "N/A";
    return (item.polarizations || []).map(pol => ({
      band: pol,
      title: `${pol} Polarization`,
      resolution: res
    }));
  }

  // LANDSAT (always 30m)
  if (satellite.includes("landsat")) {
    const spectral = [
      "coastal", "blue", "green", "red",
      "nir08", "swir16", "swir22", "lwir11"
    ];

    const res = item.resolution || 30;

    return Object.entries(assets)
      .filter(([key]) => spectral.includes(key))
      .map(([key, info]) => ({
        band: key.toUpperCase(),
        title: cleanTitle(info.title),
        resolution: res
      }));
  }

  // SENTINEL-2
  return Object.entries(assets)
    .filter(([key]) => /^B\d+$|^B8A$/i.test(key))
    .map(([key, info]) => {
      const res = extractResFromHref(info.href) || item.resolution || "N/A";

      return {
        band: key,
        title: cleanTitle(info.title),  // Clean title
        resolution: res
      };
    });
};

const getSensorType = (item) => {
  const sat = (item?.satellite || "").toLowerCase();

  if (sat.includes("sentinel-2")) return "Optical";
  if (sat.includes("sentinel-1")) return "SAR";
  if (sat.includes("landsat")) return "Optical";

  return "Unknown";
};

    // Pagination Variable
    const ITEMS_PER_PAGE = 10;

    const [visibleCounts, setVisibleCounts] = useState({});
// Sorting Config
const [sortConfig, setSortConfig] = useState({});


const sortSatelliteItems = useCallback((items, satellite) => {
  const cfg = sortConfig[satellite];
  if (!cfg || !cfg.key) return items;

  const arr = [...items];

  const getValue = (it) => {
    switch (cfg.key) {
      case 'Date':
        return new Date(it.datetime ?? it.date ?? null).getTime() || 0;
      case 'Cloud':
        return Number(it.cloud_cover ?? 0);
      case 'AOI':
        return Number(it.coveragePercentage ?? it.coverage_percentage ?? 0);
      case 'Off_nadir':
        return Number(it.off_nadir_angle ?? 0);
      case 'Sun':
        return Number(it.sun_elevation ?? it.angle ?? 0);
      default:
        return 0;
    }
  };

  return arr
    .map((v, i) => ({ v, val: getValue(v), i }))
    .sort((a, b) => {
      if (a.val === b.val) return a.i - b.i;
      return cfg.direction === 'asc' ? a.val - b.val : b.val - a.val;
    })
    .map(x => x.v);
}, [sortConfig]);

const toggleSort = (satellite, key) => {
  const safeKey = String(key); // prevent cfg.key being a function

  setSortConfig(prev => {
    const current = prev[satellite] || { key: safeKey, direction: 'desc' };

    // If changing field → start with ASC
    if (current.key !== safeKey) {
      return { ...prev, [satellite]: { key: safeKey, direction: 'asc' } };
    }

    // Toggle asc ↔ desc
    return {
      ...prev,
      [satellite]: {
        key: safeKey,
        direction: current.direction === 'asc' ? 'desc' : 'asc'
      }
    };
  });
};


    // Reset when new imagery comes in
    useEffect(() => {
  if (!items || items.length === 0) return;

  setVisibleCounts(prevCounts => {
    const updatedCounts = { ...prevCounts };
    const satellites = Array.from(new Set(items.map(i => i.satellite || 'Unknown')));

    satellites.forEach(sat => {
      // Keep previous count if exists, else initialize
      updatedCounts[sat] = prevCounts[sat] ?? ITEMS_PER_PAGE;
    });

    // Remove old satellites that no longer exist
    Object.keys(updatedCounts).forEach(sat => {
      if (!satellites.includes(sat)) delete updatedCounts[sat];
    });

    return updatedCounts;
  });
}, [items]);




  const [selectedImages, setSelectedImages] = useState([]);
  const [expandedSatellite, setExpandedSatellite] = useState(null);
  const [satelliteSelections, setSatelliteSelections] = useState({});
  const [satelliteVisibility, setSatelliteVisibility] = useState(() => {
    // Initialize all satellites as visible
    return items.reduce((acc, item) => {
      acc[item.satellite] = true;
      return acc;
    }, {});
  });

  // const handleSatelliteVisibilityToggle = (satellite) => {
  //   setSatelliteVisibility(prev => {
  //     // Get current visibility state
  //     const currentVisibility = prev[satellite] !== undefined ? prev[satellite] : true;
  //     console.log('Current visibility for', satellite, ':', currentVisibility);
  //
  //     // Toggle visibility
  //     const newVisibility = {
  //       ...prev,
  //       [satellite]: !currentVisibility
  //     };
  //
  //     // Always call parent handler with the new (toggled) state
  //     onSatelliteVisibilityToggle && onSatelliteVisibilityToggle(satellite, !currentVisibility);
  //
  //     return newVisibility;
  //   });
  // };
    const handleSatelliteVisibilityToggle = (satellite) => {
      setSatelliteVisibility(prev => {
        const newVisible = !(prev[satellite] ?? true);

        onSatelliteVisibilityToggle?.(satellite, newVisible);

        return {
          ...prev,
          [satellite]: newVisible,
        };
      });
    };
  const handleAutoSelect = async (satellite, satelliteItems, index) => {
    try {
      if (!drawnCoordinates) {
        console.error('No area of interest has been drawn');
        return;
      }
  
      const token = localStorage.getItem('token');
  
      const response = await fetch(IMAGE_URLS.getBestScenes, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          satellite,
          images: satelliteItems,
          aoi_coordinates: drawnCoordinates
        })
      });
  
      if (!response.ok) {
        throw new Error('Failed to get best scenes');
      }
  
      const data = await response.json();
      
      // Auto-select the best images
      if (data.best_image_ids && data.best_image_ids.length > 0) {
        const bestImages = satelliteItems.filter(item => 
          data.best_image_ids.includes(item.id)
        );
  
        // Use the index parameter for consistent color generation
        const satelliteColor = getCatalogSatelliteColor(satellite);
  
        // Apply selection for each best image with consistent color
        bestImages.forEach(image => {
          handleSelectImage(
            {
              ...image,
              date: formatDate(image.datetime),
              // angle: Math.round(image.sun_elevation),
              angle: `${Math.round(image.sun_elevation)}`,
              coverage: `${image.cloud_cover}`,
              resolution: `${image.resolution}`,
              previewUrl: image.preview_url,
              coordinates: image.coordinates,
              cloud_cover: image.cloud_cover,
              satellite: image.satellite,
              coveragePercentage:`${image.coveragePercentage.toFixed(1)}`,
              off_nadir_angle:`${image.off_nadir_angle}`,
              // satellite: satellite,
              coverage_percentage: image.coverage_percentage,
              satelliteColor: satelliteColor // Add the consistent satellite color
            },
            index // Pass the index to maintain color consistency
          );
        });
      }
    } catch (error) {
      console.error('Error in auto-select:', error);
      // Add your error handling here
    }
  };
  //
  // const groupedItems = items.reduce((acc, item) => {
  //   const satellite = item.satellite || 'Unknown';
  //   if (!acc[satellite]) {
  //     acc[satellite] = [];
  //   }
  //   acc[satellite].push(item);
  //   return acc;
  // }, {});
  // useEffect(() => {
  //     if (onSatelliteDataUpdate) {
  //       const satelliteColors = {};
  //       Object.entries(groupedItems).forEach(([satellite], index) => {
  //         satelliteColors[satellite] = getRandomColor(index);
  //       });
  //
  //       onSatelliteDataUpdate({
  //         groupedItems,
  //         satelliteSelections,
  //         handleSelectAllForSatellite,
  //         satelliteColors,
  //       });
  //     }
  //   }, [groupedItems, satelliteSelections]);
  // const hasAutoExpanded = useRef(false);
  //
  // useEffect(() => {
  //   if (!hasAutoExpanded.current && Object.keys(groupedItems).length > 0) {
  //     const firstSatellite = Object.keys(groupedItems)[0];
  //     setExpandedSatellite(firstSatellite);
  //     hasAutoExpanded.current = true;
  //   }
  // }, [groupedItems]);
  





  const handleSelectImage = (image, satelliteIndex) => {
    const satelliteColor = getCatalogSatelliteColor(image.satellite);
    setSelectedImages(prev => {
      const newSelectedImages = prev.some(img => img.id === image.id)
        ? prev.filter(img => img.id !== image.id)
        : [...prev, { ...image, satelliteColor: satelliteColor }];
      
      // Update satellite selections based on whether all images are selected
      const satellite = image.satellite;
      const satelliteImages = groupedItems[satellite] || [];
      const selectedSatelliteImages = newSelectedImages.filter(img => img.satellite === satellite);
      
      setSatelliteSelections(prev => ({
        ...prev,
        [satellite]: selectedSatelliteImages.length === satelliteImages.length
      }));
      
      return newSelectedImages;
    });
    
    onImageSelect && onImageSelect({ ...image, satelliteColor: satelliteColor });
  };

  const handleCollapse = (satellite) => {
    setExpandedSatellite(expandedSatellite === satellite ? null : satellite);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).toUpperCase();
  };

  // Use hardcoded colors instead of random
  const getSatelliteColor = (satelliteName) => {
    return getCatalogSatelliteColor(satelliteName);
  };

  const handleSelectAllForSatellite = (satellite, satelliteItems, index) => {
    const satelliteColor = getCatalogSatelliteColor(satellite);
    const isCurrentlySelected = satelliteSelections[satellite];

    if (isCurrentlySelected) {
      // Deselect all images for this satellite
      setSelectedImages(prev => prev.filter(img => img.satellite !== satellite));
      setSatelliteSelections(prev => ({ ...prev, [satellite]: false }));

      // For deselection, pass each image individually to match the pattern of individual selection
      satelliteItems.forEach(item => {
        onImageSelect && onImageSelect({
          ...item,
          satelliteColor,
          id: item.id,
          coordinates: item.coordinates,
          satellite: satellite
        });
      });
    } else {
      // Select all images for this satellite
      const newImages = satelliteItems.map(item => ({
        ...item,
        satelliteColor,
        id: item.id,
        date: formatDate(item.datetime),
        angle: Math.round(item.sun_elevation),
        coverage: `${item.cloud_cover}%`,
        resolution: `${item.resolution}`,
        previewUrl: item.preview_url,
        coordinates: item.coordinates,
        cloud_cover: item.cloud_cover,
        satellite: satellite,
        off_nadir_angle:item.off_nadir_angle,
        coverage_percentage: `${item.coveragePercentage.toFixed(1)}`
      }));

      setSelectedImages(prev => {
        const filteredPrev = prev.filter(img => img.satellite !== satellite);
        return [...filteredPrev, ...newImages];
      });
      setSatelliteSelections(prev => ({ ...prev, [satellite]: true }));

      // For selection, pass each image individually to match the pattern of individual selection
      newImages.forEach(image => {
        onImageSelect && onImageSelect(image);
      });
    }
  };




    // ✅ 1. Memoize groupedItems — prevents new object every render
    const groupedItems = useMemo(() => {
  const grouped = items
    .filter(item => constellationFilter.includes(item.satellite))   // 👈 FILTER HERE
    .reduce((acc, item) => {
        const satellite = item.satellite || 'Unknown';
        if (!acc[satellite]) acc[satellite] = [];
        acc[satellite].push(item);
        return acc;
      }, {});
  
  // Sort satellites in fixed order: Sentinel-2, Landsat, Sentinel-1, then others
  const sortedEntries = Object.entries(grouped).sort(([a], [b]) => sortCatalogSatellites(a, b));
  return Object.fromEntries(sortedEntries);
}, [items, constellationFilter]);

    // ✅ 2. Avoid recursive updates — only call parent when data changes
    const prevDataRef = useRef(null);

    useEffect(() => {
      if (!onSatelliteDataUpdate || Object.keys(groupedItems).length === 0) return;

      const satelliteColors = {};
      Object.entries(groupedItems).forEach(([satellite]) => {
        satelliteColors[satellite] = getCatalogSatelliteColor(satellite);
      });

      const newData = {
        groupedItems,
        satelliteSelections,
        handleSelectAllForSatellite, // ✅ using it safely if passed as prop
        satelliteColors,
      };

      // 🔒 Prevent unnecessary re-renders / recursion
      const prevData = prevDataRef.current;
      const isSame =
        prevData &&
        JSON.stringify(prevData.groupedItems) === JSON.stringify(newData.groupedItems) &&
        JSON.stringify(prevData.satelliteSelections) === JSON.stringify(newData.satelliteSelections);

      if (isSame) return;

      prevDataRef.current = newData;
      onSatelliteDataUpdate(newData);
    }, [onSatelliteDataUpdate, groupedItems, satelliteSelections, handleSelectAllForSatellite]);

    // ✅ 3. Auto-expand only once
    const hasAutoExpanded = useRef(false);

    useEffect(() => {
      if (!hasAutoExpanded.current && Object.keys(groupedItems).length > 0) {
        const firstSatellite = Object.keys(groupedItems)[0];
        setExpandedSatellite(firstSatellite);
        hasAutoExpanded.current = true;
      }
    }, [groupedItems]);
  // NEW FUNCTION: Clear all selections for a satellite
  const handleClearSelectionsForSatellite = (satellite, satelliteItems) => {
    // Only proceed if there are actually selections to clear
    const hasSelections = selectedImages.some(img => img.satellite === satellite);

    if (hasSelections) {
      // Get the selected images for this satellite
      const satelliteSelectedImages = selectedImages.filter(img => img.satellite === satellite);

      // Deselect all images for this satellite
      setSelectedImages(prev => prev.filter(img => img.satellite !== satellite));
      setSatelliteSelections(prev => ({ ...prev, [satellite]: false }));
      
      // Notify parent component about deselection of each image
      satelliteSelectedImages.forEach(image => {
        onImageSelect && onImageSelect({
          ...image,
          deselect: true // Flag to indicate this is a deselection
        });
      });
    }
  };

  return (
    <Box>
      {Object.entries(groupedItems).map(([satellite, satelliteItems], index) => {
      // console.log("Satellite:", satellite);
      // console.log("Items:", satelliteItems[0]);

      return (
        <Box key={satellite} sx={{ mb: 2 }}>
          {/*<Box sx={{ p: 1, borderBottom: '1px solid #76b6ff', borderRadius: 2 }}>*/}
          <Box sx={{ p: 1, borderBottom: '', borderRadius: 2 }}>
            {/* Clickable header section - from header start to chips end */}
            <Box 
              onClick={() => handleCollapse(satellite)}
              sx={{ 
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  // onClick={() => handleSelectAllForSatellite(satellite, satelliteItems, index)}
                    onClick={(e) => e.stopPropagation()}
                  sx={{
                    width: 15,
                    height: 15,
                      bgcolor: getCatalogSatelliteColor(satellite),
                    borderRadius: '20%',
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  {satelliteSelections[satellite] && (
                    <span style={{ 
                      color: '#fff', 
                      fontSize: '12px', 
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </span>
                  )}
                </Box>
                  <Typography sx={{ color: '#ffffff', fontSize: '12px' }}>
                  {satellite.toUpperCase()}
                </Typography>
                <Box
                    onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: 1,
                    px: 1,
                    fontSize: '11px',

                  }}
                >
                  <span>
                    {selectedImages.filter(img => 
                      satelliteItems.some(item => item.id === img.id)
                    ).length}/{satelliteItems.length} selected
                  </span>
                  
                  {/* Added Clear Selection Icon */}
                  {selectedImages.some(img => img.satellite === satellite) && (
                    <Tooltip 
                      title="Clear all selections" 
                      arrow
                      PopperProps={{ 
                        sx: { zIndex: 9999 } 
                      }}
                    >
                      <CloseIcon 
                        sx={{ 
                          fontSize: 14,
                          ml: 0.5,
                          cursor: 'pointer',
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderRadius: '50%'
                          },
                          '&:active': {
                            transform: 'scale(0.95)',
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSelectionsForSatellite(satellite, satelliteItems);
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255, 255, 255, 0.5)' }} onClick={(e) => e.stopPropagation()}>

            {/*      <Tooltip title="Sort options" arrow>*/}
            {/*          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>*/}
            {/*              <Typography sx={{ fontSize: 11, color: '#76b6ff' }}>Sort:</Typography>*/}

            {/*              <Box*/}
            {/*                  ref={sortRef}*/}
            {/*                  sx={{ position: 'relative' }}*/}
            {/*              >*/}
            {/*                  /!* Sort button label *!/*/}
            {/*                  <Button*/}
            {/*                      variant="text"*/}
            {/*                      sx={{*/}
            {/*                          color: '#fff',*/}
            {/*                          fontSize: '11px',*/}
            {/*                          textTransform: 'none',*/}
            {/*                          padding: '2px 6px',*/}
            {/*                          border: '1px solid #76b6ff50',*/}
            {/*                          borderRadius: '4px',*/}
            {/*                          minWidth: '50px',*/}
            {/*                      }}*/}
            {/*                      onClick={(e) => {*/}
            {/*                          const menu = e.currentTarget.nextSibling;*/}
            {/*                          menu.style.display =*/}
            {/*                              menu.style.display === 'block' ? 'none' : 'block';*/}
            {/*                      }}*/}
            {/*                  >*/}
            {/*                      {sortConfig.key*/}
            {/*                          ? `${sortConfig.key.toUpperCase()} ${*/}
            {/*                              sortConfig.direction === 'asc'*/}
            {/*                                  ? '↑'*/}
            {/*                                  : sortConfig.direction === 'desc'*/}
            {/*                                      ? '↓'*/}
            {/*                                      : ''*/}
            {/*                          }`*/}
            {/*                          : 'None'}*/}
            {/*                  </Button>*/}

            {/*                  /!* Dropdown menu *!/*/}
            {/*                  <Box*/}
            {/*                      className="sort-menu"*/}
            {/*                      sx={{*/}
            {/*                          display: 'none',*/}
            {/*                          position: 'absolute',*/}
            {/*                          top: '28px',*/}
            {/*                          left: 0,*/}
            {/*                          background: '#1E2126',*/}
            {/*                          border: '1px solid #76b6ff50',*/}
            {/*                          borderRadius: '6px',*/}
            {/*                          padding: '6px 0',*/}
            {/*                          zIndex: 9999,*/}
            {/*                          minWidth: '120px',*/}
            {/*                      }}*/}
            {/*                  >*/}
            {/*                      {[*/}
            {/*                          { key: 'date', label: 'Date' },*/}
            {/*                          { key: 'cloud', label: 'Cloud' },*/}
            {/*                          { key: 'aoi', label: 'AOI Coverage' },*/}
            {/*                      ].map(({ key, label }) => (*/}
            {/*                          <Box*/}
            {/*                              key={key}*/}
            {/*                              sx={{*/}
            {/*                                  px: 1.2,*/}
            {/*                                  py: 0.7,*/}
            {/*                                  fontSize: '11px',*/}
            {/*                                  color: '#fff',*/}
            {/*                                  cursor: 'pointer',*/}
            {/*                                  display: 'flex',*/}
            {/*                                  justifyContent: 'space-between',*/}
            {/*                                  '&:hover': {*/}
            {/*                                      background: 'rgba(255,255,255,0.08)',*/}
            {/*                                  },*/}
            {/*                              }}*/}
            {/*                              onClick={() => toggleSort(key)}*/}
            {/*                          >*/}
            {/*                              <span>{label}</span>*/}
            {/*                              <span>*/}
            {/*  {sortConfig.key !== key*/}
            {/*      ? ''*/}
            {/*      : sortConfig.direction === 'asc'*/}
            {/*          ? '↑'*/}
            {/*          : sortConfig.direction === 'desc'*/}
            {/*              ? '↓'*/}
            {/*              : ''}*/}
            {/*</span>*/}
            {/*                          </Box>*/}
            {/*                      ))}*/}
            {/*                  </Box>*/}
            {/*              </Box>*/}
            {/*          </Box>*/}
            {/*      </Tooltip>*/}
                  <Box onClick={(e) => e.stopPropagation()}>
                  <SortDropdown
                      satellite={satellite}
                      sortConfig={sortConfig}
                      toggleSort={toggleSort}
                    />
                  </Box>
                  {/* 👁 Tooltip for imagery toggle */}



                <Tooltip
                  title={satelliteVisibility[satellite] ? 'Hide' : 'Unhide'}
                  arrow
                  PopperProps={{ sx: { zIndex: 9999 } }}
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSatelliteVisibilityToggle(satellite);
                    }}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {satelliteVisibility[satellite] ? (
                      // 👁 Shown when visible — click will hide
                      <VisibilityIcon
                        sx={{
                          fontSize: 16,
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { color: '#fff' },
                          transition: 'color 0.2s ease',
                        }}
                      />
                    ) : (
                      // 🚫👁 Shown when hidden — click will unhide
                      <VisibilityOffIcon
                        sx={{
                          fontSize: 16,
                          color: 'rgba(255,255,255,0.4)',
                          '&:hover': { color: '#76b6ff' },
                          transition: 'color 0.2s ease',
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>

              <Tooltip
                    title={expandedSatellite === satellite ? 'Collapse all' : 'Expand all'}
                    arrow
                    PopperProps={{ sx: { zIndex: 9999 } }}
                  >
                    <KeyboardArrowUpIcon
                      sx={{ fontSize: 16, transform: expandedSatellite === satellite ? 'none' : 'rotate(180deg)'}}
                      onClick={(e) => {
                          e.stopPropagation();
                          handleCollapse(satellite);
                      }}
                    />
                </Tooltip>

              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
              <Chip 
                label={`Resolution : ${satelliteItems[0]?.resolution || 'Unknown'}m`} 
                sx={{flex: 1, bgcolor: '#438BFF80', color: 'white', fontSize: '11px', height: 18, minWidth: 110, borderRadius: "5px", textAlign: 'left', px: "0" }}
              />
{/* SENSOR TYPE CHIP */}
              <Chip 
  label={`Sensor : ${getSensorType(satelliteItems[0])}`}
  sx={{flex: 1,
    bgcolor: '#438BFF80',
    color: 'white',
    fontSize: '11px',
    height: 18,
    minWidth: 120,
    borderRadius: "5px",
    textAlign: 'left',
    px: "0"
  }}
/>

{/* BANDS / POLARIZATION CHIP */}
<Tooltip
  title={
    <Box sx={{ p: 1, maxHeight: 200, overflowY: 'auto' }}>
    {getBandDetails(satelliteItems[0]).map(({ band, title, resolution }) => (
        <Box key={band} sx={{ fontSize: '11px', mb: 0.5 }}>
            <strong>{band}</strong> — {title} — <em>{resolution}m</em>
        </Box>
      ))}
    </Box>
  }
  arrow
  PopperProps={{ sx: { zIndex: 9999 } }}
>
              <Chip 
    label={
      `Bands : ${getBandDetails(satelliteItems[0]).length}`
    }
    sx={{flex: 1,
      bgcolor: '#438BFF80',
      color: 'white',
      fontSize: '11px',
      height: 18,
      minWidth: 110,
      borderRadius: "5px",
      cursor: 'pointer',
      textAlign: 'left',
      px: "0"
    }}
              />
</Tooltip>

            </Box>
            </Box>
            {/* End of clickable header section */}

            <Collapse in={expandedSatellite === satellite}>
              <Box sx={{
                maxHeight: '200px',
                overflowY: 'auto',
                borderRadius: 1,
                p: 0.5,
                // Updated minimal scrollbar styles
                '&::-webkit-scrollbar': {
                  width: '2px',
                  backgroundColor: 'transparent'
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '1px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                },
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.1) transparent'
              }}>
                <Grid container spacing={0.5} rowSpacing={2} columnSpacing={0.5}>
                  {/*{satelliteItems.map((item) => (*/}

                {sortSatelliteItems(satelliteItems, satellite)
                  .slice(0, visibleCounts[satellite] || ITEMS_PER_PAGE)
                  .map((item) => (
                    <Grid item xs={6} key={item.id}>
                      <Box
                        sx={{
                          backgroundColor: '#1E2126', // Dark background for each tile
                          borderRadius: 1,
                          m: '5px',
                            marginTop:'0px',// 5px margin INSIDE grid item (not breaking layout)
                            marginBottom:'0px',// 5px margin INSIDE grid item (not breaking layout)
                          p: 0.5,                     // Inner padding (optional)
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#2A2E35',
                          },
                        }}
                      >
                          <ImageCard
                            data={{
                              id: item.id,
                              date: formatDate(item.datetime),
                              angle: Math.round(item.sun_elevation),
                              coverage: `${item.cloud_cover}%`,
                              resolution: `${item.resolution}m`,
                              previewUrl: item.preview_url,
                              coordinates: item.coordinates,
                              cloud_cover: item.cloud_cover,
                              satellite: item.satellite,
                              coveragePercentage:item.coveragePercentage.toFixed(1),
                              off_nadir_angle:item.off_nadir_angle,
                              coverage_percentage: item.coverage_percentage,
                              satelliteColor: getCatalogSatelliteColor(satellite)
                            }}
                            isSelected={selectedImages.some(img => img.id === item.id)}
                            isVisible={visibleImage && visibleImage.id === item.id}
                            onSelect={(image) => handleSelectImage(image, index)}
                            onVisibilityToggle={onVisibilityToggle}
                            onHover={onImageHover}
                          />
                      </Box>

                    </Grid>
                  ))}
                </Grid>
                {/* Load More Button */}
                {(visibleCounts[satellite] || 0) < satelliteItems.length && (
                  <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        setVisibleCounts(prev => {
                          const current = prev[satellite] || ITEMS_PER_PAGE;
                          const total = groupedItems[satellite]?.length || 0;
                          const newCount = Math.min(current + ITEMS_PER_PAGE, total);
                          return { ...prev, [satellite]: newCount };
                        })
                      }
                      sx={{
                        color: '#76b6ff',
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 500,
                        '&:hover': {
                          color: '#2E90FA',
                          backgroundColor: 'rgba(46,144,250,0.1)',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        mx: 'auto',
                      }}
                    >
                      Load More
                      <KeyboardArrowDownIcon fontSize="small" />
                    </Button>

                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      );
      })}
    </Box>
  );
};

export default CatalogImagery;